import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  can,
  formatPercent,
  formatRial,
  parseTranches,
  renderPaymentTerms,
  STATE_LABELS_FA,
  tranchesTotalPct,
  TRANSITIONS,
  type DealState,
  type PaymentTranche,
} from '@kia/shared';
import { api, getAuthToken } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useNotifications } from '../messaging/NotificationProvider';
import { useMarginFloors } from '../config/ConfigProvider';
import { marginColor } from '../lib/margin';
import { pricingFor } from '../lib/pricing';
import { lineIsEstimated } from '../lib/lineProvenance';
import { isReleasePrepEligible } from '../lib/releasePrep';
import {
  Button,
  Card,
  CostConfidence,
  EmptyState,
  EntityPicker,
  ErrorRetry,
  Field,
  Input,
  Modal,
  StatusBadge,
  Textarea,
  useToast,
  NumberInput,
  JalaliDateInput,
  ReleaseVerdictChip,
  ProvisionalBookBanner,
} from '../ui';
import { useApiResource } from '../lib/useApiResource';
import type { View } from '../nav/navModel';
import { AttentionStrip } from './AttentionStrip';
import { BomModal } from './BomModal';
import { DealExtras, type DealExtrasData } from './DealExtras';
import { ScheduleEditor, type Template } from './ScheduleEditor';
import { dealAnalysisSeed } from './aiSeed';
import type { DealPrefill } from './InquiriesScreen';

interface DealListRow {
  id: number;
  no: string;
  state: DealState;
  customer_name: string | null;
  revenue: number;
  gross: number;
  margin: number | null;
  warns: string[];
  itemCount: number;
}
interface ProductSummary {
  code: string;
  name: string;
  family: string;
  fullCost: number;
  confirmed: number;
  missing: number;
  listPrice: number;
  maxDiscount: number;
  // Cost-completeness per BOM line (E16-02) — the server already sends these; the item picker reads them
  // so the owner sees how trustworthy a product's COGS is BEFORE adding the line to the deal.
  actualLines: number;
  groupLines: number;
  missingLines: number;
}
interface Customer {
  id: number;
  name: string;
}
interface FormItem {
  product_code: string;
  name: string;
  qty: string;
  /** gross unit price (before the per-line discount) */ price: string;
  /** absolute rial discount off the line total (WS-C, note #1) */ discount?: string;
  unitFull: number;
}
interface DealDetail extends DealExtrasData {
  id: number;
  no: string;
  state: DealState;
  version: number;
  customer_id: number | null;
  validity: string;
  ship_terms: string;
  pay_terms: string;
  penalty: string;
  price_fixed: number;
  notes: string;
  remarks: string;
  payment_schedule: string | null;
  expected_delivery: string | null;
  /** frozen gross margin the deal was offered at (Move 3) — the immutable anchor vs today's live margin */
  offer_margin: number | null;
  compute: DealExtrasData['compute'] & { warnings: string[] };
}

export function DealsScreen({
  prefill = null,
  onPrefillConsumed,
  openDealId = null,
  onDealOpened,
  onIssueInvoice,
  onPrepareRelease,
  onOpenAnalyst,
  onOpenDeal,
  onOpenCustomer,
  onNavigate,
}: {
  prefill?: DealPrefill | null;
  onPrefillConsumed?: () => void;
  openDealId?: number | null;
  onDealOpened?: () => void;
  /** issue a مودیان tax invoice from this deal (E13-10) — provided only to finance roles. */
  onIssueInvoice?: ((dealId: number) => void) | undefined;
  /** seed a release-prep (حواله) from this deal (FLOW-1) — provided only to release.prepare roles. */
  onPrepareRelease?: ((dealId: number) => void) | undefined;
  /** seed «دستیار تحلیل» with a question about this deal (AI-3) — provided only to ai.use (Admin). */
  onOpenAnalyst?: ((seed: string) => void) | undefined;
  // UX-1 «کارهای امروز» strip nav — threaded from AppShell, mirrors DashboardScreen's callbacks so an
  // attention row routes to the record / view identically here.
  onOpenDeal?: ((id: number) => void) | undefined;
  onOpenCustomer?: ((customerId: number) => void) | undefined;
  onNavigate?: ((view: View, focusDealId?: number) => void) | undefined;
}) {
  // Capture the inbox prefill once; the shell clears its copy immediately so
  // revisiting this tab later starts clean.
  const [seed] = useState(prefill);
  const [editing, setEditing] = useState<number | 'new' | null>(seed ? 'new' : null);
  useEffect(() => {
    if (seed) onPrefillConsumed?.();
  }, [seed, onPrefillConsumed]);
  // A global-search hit deep-links a specific deal open.
  useEffect(() => {
    if (openDealId != null) {
      setEditing(openDealId);
      onDealOpened?.();
    }
  }, [openDealId, onDealOpened]);
  if (editing === 'new')
    return <DealForm dealId={null} prefill={seed} onBack={() => setEditing(null)} />;
  if (typeof editing === 'number')
    return (
      <DealForm
        dealId={editing}
        onBack={() => setEditing(null)}
        onIssueInvoice={onIssueInvoice}
        onPrepareRelease={onPrepareRelease}
        onOpenAnalyst={onOpenAnalyst}
      />
    );
  return (
    <DealList
      onNew={() => setEditing('new')}
      onOpen={setEditing}
      onOpenDeal={onOpenDeal}
      onOpenCustomer={onOpenCustomer}
      onNavigate={onNavigate}
    />
  );
}

function DealList({
  onNew,
  onOpen,
  onOpenDeal,
  onOpenCustomer,
  onNavigate,
}: {
  onNew: () => void;
  onOpen: (id: number) => void;
  onOpenDeal?: ((id: number) => void) | undefined;
  onOpenCustomer?: ((customerId: number) => void) | undefined;
  onNavigate?: ((view: View, focusDealId?: number) => void) | undefined;
}) {
  const res = useApiResource<DealListRow[]>('/api/deals');
  const rows = res.data ?? [];
  const floors = useMarginFloors();
  return (
    <>
      <AttentionStrip
        variant="collapsible"
        onOpenDeal={onOpenDeal}
        onOpenCustomer={onOpenCustomer}
        onNavigate={onNavigate}
      />
      <Card>
        <Button onClick={onNew}>+ پیشنهاد / قرارداد جدید</Button>
      </Card>
      <Card title={`قراردادها و پیشنهادها (${rows.length})`}>
        {res.status === 'error' ? (
          <ErrorRetry error={res.error} onRetry={res.reload} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>شماره / مشتری</th>
                <th>وضعیت</th>
                <th>درآمد</th>
                <th>سود ناخالص</th>
                <th>حاشیه</th>
                <th>هشدار</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon="📄"
                      title="هنوز پیشنهاد یا قراردادی نیست"
                      hint="اولین پیشنهاد قیمت را با دکمهٔ «پیشنهاد جدید» بالای همین صفحه بسازید؛ پس از تأیید مشتری، به قرارداد تبدیل می‌شود."
                    />
                  </td>
                </tr>
              ) : (
                rows.map((d) => (
                  <tr key={d.id}>
                    <td>
                      {d.no}
                      <div className="muted">{d.customer_name || '—'}</div>
                    </td>
                    <td>
                      <StatusBadge state={d.state} />
                    </td>
                    <td>{formatRial(d.revenue)}</td>
                    <td>{formatRial(d.gross)}</td>
                    <td style={{ color: marginColor(d.margin, floors), fontWeight: 600 }}>
                      {formatPercent(d.margin)}
                    </td>
                    <td>
                      {d.warns.slice(0, 2).map((w, i) => (
                        <div key={i} className="warn">
                          ⚠ {w}
                        </div>
                      )) || null}
                    </td>
                    <td>
                      <Button variant="ghost" onClick={() => onOpen(d.id)}>
                        باز کردن
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

function ProductSearch({ onAdd }: { onAdd: (p: ProductSummary) => void }) {
  return (
    <EntityPicker<ProductSummary>
      label="افزودن محصول (کد یا نام)"
      endpoint="/api/products"
      maxResults={8}
      itemKey={(p) => p.code}
      renderResult={(p) => ({
        label: p.name || p.code,
        // Show the cost AND how trustworthy it is (E16-02) right in the result, so the owner never adds a
        // line blind to a missing/estimated COGS. Same CostConfidence chip the master list uses.
        subtitle: (
          <>
            ({p.code}) — بها {formatRial(p.fullCost)}{' '}
            <CostConfidence
              confirmed={p.confirmed}
              actualLines={p.actualLines}
              groupLines={p.groupLines}
              missingLines={p.missingLines}
            />
          </>
        ),
      })}
      onSelect={onAdd}
    />
  );
}

function DealForm({
  dealId,
  prefill = null,
  onBack,
  onIssueInvoice,
  onPrepareRelease,
  onOpenAnalyst,
}: {
  dealId: number | null;
  prefill?: DealPrefill | null;
  onBack: () => void;
  onIssueInvoice?: ((dealId: number) => void) | undefined;
  onPrepareRelease?: ((dealId: number) => void) | undefined;
  onOpenAnalyst?: ((seed: string) => void) | undefined;
}) {
  const toast = useToast();
  const { user } = useAuth();
  const { openMessaging } = useNotifications();
  const floors = useMarginFloors();
  // Generating a customer-facing quote/proforma is doc.write (distinct from reading the deal) — hide the
  // print buttons for roles that lack it, matching the server guard (RBAC hardening).
  const canDoc = user ? can(user.role, 'doc.write') : false;
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [floor, setFloor] = useState(0.35);
  // The margin the owner AIMS for (the suggested price hits it); defaults to the floor until raised.
  const [target, setTarget] = useState(0.35);
  // UX-3: the book's set-up health — the SAME signal the dashboard's costBasis reads
  // (params.health on /api/assumptions/month). When not healthy (pool_unset et al.) every number here
  // is material-only/provisional, surfaced by the calm ProvisionalBookBanner at the top of the form.
  const [bookProvisional, setBookProvisional] = useState(false);
  const [validityMaxDays, setValidityMaxDays] = useState(7);
  const [deal, setDeal] = useState<DealDetail | null>(null);
  const [customerId, setCustomerId] = useState('');
  // The selected customer's live credit exposure (TKT-6) — shown at the quote moment so Sales prices knowing
  // who already owes us / is over their limit, instead of discovering it only after the deal is saved.
  const [custCredit, setCustCredit] = useState<{
    exposure: number;
    limit: number;
    over: boolean;
    overBy: number;
  } | null>(null);
  const [validity, setValidity] = useState('');
  const [shipTerms, setShipTerms] = useState('');
  const [payTerms, setPayTerms] = useState('');
  const [penalty, setPenalty] = useState('');
  const [notes, setNotes] = useState('');
  const [remarks, setRemarks] = useState('');
  const [schedule, setSchedule] = useState<PaymentTranche[]>([]);
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [termDefaults, setTermDefaults] = useState<{
    pay_terms: string;
    ship_terms: string;
    penalty: string;
    notes: string;
    remarks: string;
    validity_days: number;
  } | null>(null);
  const [items, setItems] = useState<FormItem[]>([]);
  const [bomCode, setBomCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [docPreview, setDocPreview] = useState<'quote' | 'proforma' | null>(null);
  const inquiryLinked = useRef(false);

  const hydrate = useCallback((d: DealDetail) => {
    setDeal(d);
    setCustomerId(d.customer_id ? String(d.customer_id) : '');
    setValidity(d.validity);
    setShipTerms(d.ship_terms);
    setPayTerms(d.pay_terms);
    setPenalty(d.penalty);
    setNotes(d.notes);
    setRemarks(d.remarks ?? '');
    setSchedule(parseTranches(d.payment_schedule));
    setExpectedDelivery(d.expected_delivery ?? '');
    setItems(
      d.compute.lines.map((l) => ({
        product_code: l.product_code,
        name: l.name,
        qty: String(l.qty),
        // The form edits the GROSS price + the discount; the API's `price` is the net effective price.
        price: String(l.grossUnitPrice),
        discount: l.discount ? String(l.discount) : '',
        unitFull: l.unitFull,
      })),
    );
  }, []);

  // Fetch the selected customer's exposure whenever it changes (TKT-6) — the decision context for pricing.
  useEffect(() => {
    if (!customerId) {
      setCustCredit(null);
      return;
    }
    let live = true;
    void api<{ exposure: number; limit: number; over: boolean; overBy: number }>(
      `/api/customers/${customerId}/exposure`,
    ).then((r) => {
      if (live && r.ok)
        setCustCredit(r.body as { exposure: number; limit: number; over: boolean; overBy: number });
    });
    return () => {
      live = false;
    };
  }, [customerId]);

  useEffect(() => {
    void api<Customer[]>('/api/customers').then((r) => {
      if (r.ok && Array.isArray(r.body)) setCustomers(r.body);
    });
    // The pricing floor is MONTH-resolved (Assumption Master override ?? config) — one authority with the
    // server's margin warnings, so the suggested price and the warning can never disagree.
    void api<{
      params: {
        floor: number;
        target: number;
        health?: { healthy: boolean } | undefined;
      };
    }>('/api/assumptions/month').then((r) => {
      if (r.ok) {
        const p = (
          r.body as {
            params: {
              floor: number;
              target: number;
              health?: { healthy: boolean } | undefined;
            };
          }
        ).params;
        if (Number.isFinite(p.floor) && p.floor > 0) setFloor(p.floor);
        // Aim at the target; fall back to the floor when target isn't set higher (server defaults target=floor).
        setTarget(Number.isFinite(p.target) && p.target > 0 ? p.target : p.floor);
        // UX-3: fail-silent — only the explicit `healthy === false` raises the provisional banner.
        setBookProvisional(p.health ? !p.health.healthy : false);
      }
    });
    void api<{ piValidityMaxDays: number }>('/api/config').then((r) => {
      if (r.ok) {
        const cfg = r.body as { piValidityMaxDays: number };
        if (Number.isFinite(cfg.piValidityMaxDays)) setValidityMaxDays(cfg.piValidityMaxDays);
      }
    });
    void api<NonNullable<typeof termDefaults>>('/api/doc-terms').then((r) => {
      if (r.ok) setTermDefaults(r.body as NonNullable<typeof termDefaults>);
    });
    void api<Template[]>('/api/payment-templates').then((r) => {
      if (r.ok && Array.isArray(r.body)) setTemplates(r.body as Template[]);
    });
    if (dealId != null) {
      void api<DealDetail>(`/api/deals/${dealId}`).then((r) => {
        if (r.ok) hydrate(r.body as DealDetail);
      });
    }
  }, [dealId, hydrate]);

  // Seed a brand-new form from a website inquiry («تبدیل به پیشنهاد»).
  const prefillApplied = useRef(false);
  useEffect(() => {
    if (dealId != null || !prefill || prefillApplied.current) return;
    prefillApplied.current = true;
    if (prefill.customerId) setCustomerId(String(prefill.customerId));
    if (prefill.notes) setNotes(prefill.notes);
    const q = prefill.productCode || prefill.productName;
    if (!q) return;
    void api<ProductSummary[]>(`/api/products?q=${encodeURIComponent(q)}`).then((r) => {
      const list = r.ok && Array.isArray(r.body) ? (r.body as ProductSummary[]) : [];
      const p = list.find((x) => x.code === prefill.productCode) ?? list[0];
      const qty = String(prefill.qty > 0 ? prefill.qty : 1);
      if (p) {
        const suggested =
          p.listPrice > 0
            ? p.listPrice
            : p.fullCost > 0
              ? Math.round(p.fullCost / (1 - target))
              : 0;
        setItems([
          {
            product_code: p.code,
            name: p.name || p.code,
            qty,
            price: suggested ? String(suggested) : '',
            unitFull: p.fullCost,
          },
        ]);
      } else if (prefill.productCode) {
        // Not in the product master yet — keep the requested code visible for pricing.
        setItems([
          {
            product_code: prefill.productCode,
            name: prefill.productName,
            qty,
            price: '',
            unitFull: 0,
          },
        ]);
      }
    });
    // one-shot seed; floor is only a suggestion input, so it is deliberately not a dependency
  }, [dealId, prefill]);

  const reload = useCallback(() => {
    if (!deal) return;
    void api<DealDetail>(`/api/deals/${deal.id}`).then((r) => {
      if (r.ok) hydrate(r.body as DealDetail);
    });
  }, [deal, hydrate]);

  function addProduct(p: ProductSummary) {
    // The market list price wins as the suggestion; fall back to cost cleared over the floor.
    const suggested =
      p.listPrice > 0 ? p.listPrice : p.fullCost > 0 ? Math.round(p.fullCost / (1 - target)) : 0;
    setItems((prev) => [
      ...prev,
      {
        product_code: p.code,
        name: p.name || p.code,
        qty: '1',
        price: suggested ? String(suggested) : '',
        unitFull: p.fullCost,
      },
    ]);
  }
  const setItem = (i: number, k: 'qty' | 'price' | 'discount', v: string) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const removeItem = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const totals = useMemo(() => {
    let rev = 0;
    let cost = 0;
    for (const it of items) {
      const lineGross = (Number(it.qty) || 0) * (Number(it.price) || 0);
      rev += Math.max(0, lineGross - (Number(it.discount) || 0)); // net of the per-line discount
      cost += (Number(it.qty) || 0) * it.unitFull;
    }
    return { rev, cost, gross: rev - cost, margin: rev > 0 ? (rev - cost) / rev : null };
  }, [items]);

  async function save(): Promise<DealDetail | null> {
    setError('');
    const valid = items.filter((i) => i.product_code);
    if (!valid.length) {
      setError('حداقل یک قلم لازم است');
      return null;
    }
    for (const i of valid) {
      if (!(Number(i.qty) > 0)) {
        setError(`تعداد باید بزرگ‌تر از صفر باشد: ${i.name}`);
        return null;
      }
      if (!(Number(i.price) > 0)) {
        setError(`قیمت فروش باید بزرگ‌تر از صفر باشد: ${i.name}`);
        return null;
      }
    }
    // A non-empty payment schedule must cover the whole contract (Σ = 100%) — the same rule the template
    // manager already enforces. Otherwise the cash-calendar receivable projection is built on a partial or
    // over-committed schedule. The server re-validates (schemas.ts refine); this is the immediate feedback.
    if (schedule.length && Math.round(tranchesTotalPct(schedule)) !== 100) {
      setError('مجموع درصدهای زمان‌بندی پرداخت باید ۱۰۰٪ شود');
      return null;
    }
    setBusy(true);
    const payload: Record<string, unknown> = {
      customer_id: customerId ? Number(customerId) : null,
      validity,
      ship_terms: shipTerms,
      pay_terms: payTerms,
      penalty,
      notes,
      remarks,
      payment_schedule: schedule,
      expected_delivery: expectedDelivery,
      items: valid.map((i) => ({
        product_code: i.product_code,
        qty: Number(i.qty),
        price: Number(i.price),
        discount: Number(i.discount) || 0,
      })),
    };
    if (deal) {
      payload.id = deal.id;
      payload.version = deal.version;
    }
    const r = await api<DealDetail>('/api/deals', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    setBusy(false);
    if (r.status === 409) {
      toast('این پیشنهاد همین لحظه تغییر کرد — بارگذاری دوباره شد.', 'error');
      // Re-FETCH the fresh row: onOpen(deal.id) was a no-op here (same id ⇒ the mount effect never
      // re-fired), so the form kept the stale version and every retry 409'd again.
      reload();
      return null;
    }
    if (!r.ok) {
      setError((r.body as { message?: string }).message ?? 'خطا در ذخیره');
      return null;
    }
    toast('ذخیره شد');
    const saved = r.body as DealDetail;
    hydrate(saved);
    if (prefill?.inquiryId && !inquiryLinked.current) {
      inquiryLinked.current = true;
      void api(`/api/inquiries/${prefill.inquiryId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'converted', deal_id: saved.id }),
      }).then((res) => {
        if (res.ok) toast('استعلام سایت به این پیشنهاد متصل شد');
      });
    }
    return saved;
  }

  /** Save first, then open the print preview — one motion (ذخیره و چاپ). */
  async function saveAndPreview(kind: 'quote' | 'proforma') {
    const saved = await save();
    if (saved) setDocPreview(kind);
  }

  const docUrl = (kind: 'quote' | 'proforma', download = false) =>
    `/api/deals/${deal?.id}/document?kind=${kind}&t=${getAuthToken() ?? ''}${download ? '&download=1' : ''}`;

  async function transition(to: DealState) {
    if (!deal) return;
    const reason = to === 'Lost' || to === 'Cancelled' ? (window.prompt('دلیل؟') ?? '') : undefined;
    const r = await api<DealDetail>(`/api/deals/${deal.id}/transition`, {
      method: 'POST',
      body: JSON.stringify({ to, reason }),
    });
    if (!r.ok)
      return toast((r.body as { message?: string }).message ?? 'تغییر وضعیت مجاز نیست', 'error');
    toast(`وضعیت تغییر کرد: ${STATE_LABELS_FA[to]}`);
    hydrate(r.body as DealDetail);
  }

  const legalNext = deal ? (TRANSITIONS[deal.state] ?? []) : [];
  const belowFloor = totals.margin != null && totals.margin < floor;

  // Item-lock (E16-20): pre-emptively disable item editing + NAME why, instead of only erroring on save.
  const itemsLocked = deal?.itemsLock?.locked ?? false;
  const lockReasonFa = (r: 'deliveries' | 'payments' | 'release'): string =>
    r === 'deliveries'
      ? 'تحویل فعال ثبت‌شده (برای اصلاح، ابتدا تحویل‌ها را برگردانید)'
      : r === 'payments'
        ? 'پرداخت تسویه‌شده قیمت را قفل کرده (ابتدا وضعیت چک/دریافت را برگردانید)'
        : 'آزادسازی انبار در جریان است (ابتدا آن را لغو کنید)';

  return (
    <>
      {/* UX-3: one calm read-only provisional-book notice, keyed on the dashboard's own health signal
          (params.health.healthy). Supersedes the former loud, hand-rolled conversionRate===0 warning so the
          quote screen carries ONE provisional signal, not two. */}
      <ProvisionalBookBanner show={bookProvisional} />
      <Card
        title={
          deal ? `پیشنهاد ${deal.no} — ${STATE_LABELS_FA[deal.state]}` : 'پیشنهاد / قرارداد جدید'
        }
      >
        <div className="form-grid">
          <Field label="مشتری" hint="برای قطعی‌سازی (تبدیل به قرارداد) الزامی است">
            <select
              className="input"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
            >
              <option value="">— انتخاب —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {custCredit ? (
              <div style={{ marginTop: 6, fontSize: '.85em' }}>
                بدهی جاری این مشتری: <b className="num">{formatRial(custCredit.exposure)}</b> ریال
                {custCredit.limit > 0 ? (
                  custCredit.over ? (
                    <span style={{ color: 'var(--red)', fontWeight: 600 }}>
                      {' '}
                      · ⛔ فراتر از سقف اعتبار ({formatRial(custCredit.overBy)} بیشتر)
                    </span>
                  ) : (
                    <span className="muted"> · سقف {formatRial(custCredit.limit)}</span>
                  )
                ) : (
                  <span className="muted"> · سقف اعتباری تنظیم نشده</span>
                )}
              </div>
            ) : null}
          </Field>
          <Field
            label="اعتبار پیشنهاد (تا تاریخ)"
            hint={`حداکثر ${validityMaxDays} روز؛ ${
              termDefaults && termDefaults.validity_days > 0
                ? `خالی = ${termDefaults.validity_days} روز از تاریخ صدور`
                : 'قیمت فقط تا این تاریخ تضمین است'
            }`}
          >
            <JalaliDateInput
              value={validity}
              max={new Date(Date.now() + validityMaxDays * 864e5).toISOString().slice(0, 10)}
              onValue={setValidity}
            />
          </Field>
          <Field label="شرایط ارسال/تحویل" hint="خالی = متن پیش‌فرض تنظیمات چاپ می‌شود">
            <Input
              value={shipTerms}
              onChange={(e) => setShipTerms(e.target.value)}
              placeholder={termDefaults?.ship_terms || ''}
            />
          </Field>
          <Field
            label="شرایط پرداخت (متن آزاد)"
            hint="فقط برای قراردادهای بدون زمان‌بندی؛ اگر «زمان‌بندی پرداخت» پر باشد، متن از روی آن ساخته و چاپ می‌شود"
          >
            <Input
              value={payTerms}
              onChange={(e) => setPayTerms(e.target.value)}
              placeholder={termDefaults?.pay_terms || 'مثلاً ۶۰ روز / چک'}
            />
          </Field>
          <Field label="جریمهٔ تأخیر" hint="خالی = متن پیش‌فرض تنظیمات چاپ می‌شود">
            <Input
              value={penalty}
              onChange={(e) => setPenalty(e.target.value)}
              placeholder={termDefaults?.penalty || ''}
            />
          </Field>
          <div className="field">
            <label style={{ fontWeight: 600, fontSize: 12, color: 'var(--ink-2)' }}>
              قیمت‌گذاری
            </label>
            <p className="muted" style={{ margin: '2px 0 0', fontSize: 11 }}>
              تثبیت قیمت خودکار بر اساس پیش‌پرداخت واقعی محاسبه می‌شود؛ سهم پرداخت‌شده به نرخ همان
              روز قفل و مابقی شناور می‌ماند (به تفکیک در کارت دریافتنی). متن بندِ چاپیِ این قاعده در
              «تنظیمات ← شرایط اسناد» قابل ویرایش است.
            </p>
          </div>
        </div>

        <div
          style={{
            border: '1px solid var(--line, #e5e7eb)',
            borderRadius: 8,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <b style={{ fontSize: 13 }}>زمان‌بندی پرداخت (پیش‌بینی وصول در تقویم نقدینگی)</b>
            <div style={{ minWidth: 180 }}>
              <Field label="تاریخ تحویل موردانتظار">
                <JalaliDateInput value={expectedDelivery} onValue={setExpectedDelivery} />
              </Field>
            </div>
          </div>
          <p className="muted" style={{ margin: '0 0 8px', fontSize: 12 }}>
            هر ردیف سهمی از مبلغ قرارداد است؛ «از سفارش» یا «از تحویل» + تعداد روز. خالی = پیش‌فرض
            تنظیمات. چک‌های واقعی جای برآورد را می‌گیرند.
          </p>
          <ScheduleEditor tranches={schedule} onChange={setSchedule} templates={templates} />
          {schedule.length ? (
            <p
              className="muted"
              style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.8 }}
              title="این متن روی پیش‌فاکتور چاپ می‌شود"
            >
              <b>شرایط پرداخت چاپ‌شده: </b>
              {renderPaymentTerms(schedule) || '—'}
            </p>
          ) : null}
        </div>

        <Field label="یادداشت">
          <Textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={termDefaults?.notes || ''}
          />
        </Field>
        <Field label="ملاحظات (پای سند چاپ می‌شود)" hint="خالی = متن پیش‌فرض تنظیمات چاپ می‌شود">
          <Textarea
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder={termDefaults?.remarks || ''}
          />
        </Field>

        <div
          style={{ fontWeight: 700, color: 'var(--navy-l)', margin: '16px 0 6px', fontSize: 14 }}
        >
          اقلام
        </div>
        {itemsLocked ? (
          <div className="banner banner-warn" style={{ marginBottom: 8 }}>
            <b>اقلام این قرارداد قفل است.</b>{' '}
            {(deal?.itemsLock?.reasons ?? []).map(lockReasonFa).join(' · ')}. سایر اطلاعات (مشتری،
            شرایط، یادداشت) قابل ویرایش است.
          </div>
        ) : (
          <ProductSearch onAdd={addProduct} />
        )}
        {items.length ? (
          <div className="dealitem-head" style={{ marginTop: 10 }}>
            <div>محصول</div>
            <div>
              تعداد{' '}
              <span className="field-req" title="الزامی">
                *
              </span>
            </div>
            <div>
              قیمت فروش / تخفیف (﷼){' '}
              <span className="field-req" title="الزامی">
                *
              </span>
            </div>
            <div>بها / سود</div>
            <div></div>
          </div>
        ) : null}
        {items.map((it, i) => {
          const q = Number(it.qty) || 0;
          const lineNetRev = Math.max(0, q * (Number(it.price) || 0) - (Number(it.discount) || 0));
          const lineGross = lineNetRev - q * it.unitFull; // profit net of the per-line discount
          // Inline pricing guidance (E16-08) via the shared, unit-tested helper — the margin this price yields
          // (vs the floor) and the price that would hit the floor, so the owner never prices a line blind.
          const {
            margin: lineMargin,
            belowFloor: belowLineFloor,
            suggested,
          } = pricingFor(it.unitFull, q > 0 ? lineNetRev / q : 0, floor, target);
          // UX-5: the saved computed line carries the cost-provenance flags. When its COGS is estimated
          // (un-priced group, or confidence below confFloor) the بها shown is likely UNDERstated. Reuse the
          // one web predicate (never a client confFloor). A `missing` line already shows the ⛔ hard block
          // below, so the quiet «⚠ تقریبی» mark is for the priced-but-estimated OK line — invisible today.
          const savedLine = deal?.compute?.lines?.find((l) => l.product_code === it.product_code);
          const lineEstimated =
            !!savedLine && lineIsEstimated(savedLine) && !savedLine.missing.length;
          return (
            <div className="dealitem" key={i}>
              <div className="pname">
                {it.name}
                <div className="muted">{it.product_code}</div>
              </div>
              <div>
                <NumberInput
                  allowDecimal
                  value={it.qty}
                  onValue={(n) => setItem(i, 'qty', n ? String(n) : '')}
                  disabled={itemsLocked}
                />
              </div>
              <div>
                <NumberInput
                  value={it.price}
                  onValue={(n) => setItem(i, 'price', n ? String(n) : '')}
                  disabled={itemsLocked}
                />
                <div
                  style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}
                  title="تخفیف مطلق روی مبلغ این ردیف (﷼)"
                >
                  <span className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    تخفیف
                  </span>
                  <NumberInput
                    value={it.discount ?? ''}
                    onValue={(n) => setItem(i, 'discount', n ? String(n) : '')}
                    disabled={itemsLocked}
                  />
                </div>
              </div>
              <div className="pcalc">
                بها: {formatRial(it.unitFull)}
                {lineEstimated ? (
                  <span
                    className="muted"
                    style={{ color: 'var(--amber)', marginInlineStart: 6 }}
                    title="بهای این قلم بیشتر تخمینی است (اطمینان بها پایین) — ممکن است کمتر از واقعی باشد"
                  >
                    ⚠ تقریبی
                  </span>
                ) : null}
                <br />
                حاشیه:{' '}
                <b style={{ color: marginColor(lineMargin, floors) }}>
                  {formatPercent(lineMargin)}
                </b>
                {belowLineFloor ? (
                  <span className="warn" title={`زیر کف ${formatPercent(floor)}`}>
                    {' '}
                    ⚠
                  </span>
                ) : null}
                <br />
                <span title="قیمتی که دقیقاً به هدف حاشیهٔ سود می‌رسد: بها ÷ (۱ − هدف)">
                  پیشنهاد (هدف {formatPercent(target)}):{' '}
                  {suggested > 0 ? (
                    <button
                      type="button"
                      className="linklike"
                      onClick={() => setItem(i, 'price', String(suggested))}
                      title="گذاشتن این قیمت روی ردیف"
                      disabled={itemsLocked}
                    >
                      {formatRial(suggested)}
                    </button>
                  ) : (
                    '—'
                  )}
                </span>
                <br />
                سود: {formatRial(lineGross)} ·{' '}
                <button
                  type="button"
                  className="linklike"
                  onClick={() => setBomCode(it.product_code)}
                >
                  BOM
                </button>
                {/* Release-gate verdict at PRICING time (E16-09): the SAME gate the warehouse enforces,
                    surfaced here so a line that can't ship (بها ناقص / سربارِ ماه ثبت‌نشده / زیر کف) is seen
                    while the price is still editable — not first at dispatch. Reflects the deal's saved state
                    (refreshes on save); the live margin above it updates per keystroke. `hideOk` keeps the row
                    quiet until there is something to act on. */}
                {savedLine?.release ? (
                  <>
                    <br />
                    <ReleaseVerdictChip release={savedLine.release} hideOk showReason />
                  </>
                ) : null}
              </div>
              <div>
                <Button variant="danger" onClick={() => removeItem(i)} disabled={itemsLocked}>
                  ✕
                </Button>
              </div>
            </div>
          );
        })}

        <div className="totalbox">
          <b>جمع:</b> درآمد {formatRial(totals.rev)} · بهای تمام‌شده {formatRial(totals.cost)} ·{' '}
          <b>سود ناخالص {formatRial(totals.gross)}</b> · حاشیه{' '}
          <b style={{ color: marginColor(totals.margin, floors) }}>
            {formatPercent(totals.margin)}
          </b>{' '}
          {belowFloor ? <span className="warn">⚠ زیر حد مجاز</span> : null}
          {/* UX-5 header grain: a glanceable mark on the deal margin when ANY saved line's COGS is estimated —
              so the headline margin reads with the caveat the full-sentence banner below spells out. Keyed on
              the one web predicate over the deal's saved lines (no client confFloor). */}
          {deal?.compute?.lines?.some(lineIsEstimated) ? (
            <span
              className="muted"
              style={{ color: 'var(--amber)', fontSize: 12 }}
              title="حاشیه بر پایهٔ اقلامِ تخمینی است — بها ممکن است کمتر از واقعی باشد"
            >
              {' '}
              ⚠ تقریبی
            </span>
          ) : null}
          {/* The frozen offer-margin anchor (Move 3): «what we quoted», immutable, beside today's live margin —
              so the owner reads the estimate-vs-now gap directly. Shown once the deal has been offered. */}
          {deal?.offer_margin != null ? (
            <span
              className="muted"
              style={{ fontSize: 12 }}
              title="حاشیهٔ منجمدِ لحظهٔ پیشنهاد — لنگر ثابت برای مقایسه با حاشیهٔ امروز"
            >
              {' · '}حاشیهٔ پیشنهاد (منجمد):{' '}
              <b style={{ color: marginColor(deal.offer_margin, floors) }}>
                {formatPercent(deal.offer_margin)}
              </b>
            </span>
          ) : null}
        </div>
        {error ? <div className="field-error">{error}</div> : null}
        {/* Dollar view (owner idea) — INTERNAL, informational; deliberately NOT on the printed quote/invoice.
            The engine stays margin-preserving; this only SHOWS the deal in USD + the remainder's dollar gap. */}
        {deal?.dollarView
          ? (() => {
              const dv = deal.dollarView!;
              const usd = (n: number) => '$' + Math.round(n).toLocaleString('en-US');
              const eroded = dv.gapRial > 0;
              return (
                <div
                  className="totalbox"
                  style={{ marginTop: 6, background: 'var(--surface-2, #f5f5f7)' }}
                  title="فقط برای اطلاع شما — روی پیش‌فاکتور/فاکتور چاپ نمی‌شود"
                >
                  <b>ارزش دلاری (فقط اطلاع):</b> پیشنهاد {usd(dv.offerUsd)} در نرخ{' '}
                  {formatRial(dv.usdAtOffer)} · دلار امروز {formatRial(dv.usdNow)} (
                  {dv.usdChangePct >= 0 ? '+' : ''}
                  {formatPercent(dv.usdChangePct)})
                  {dv.remainderUsdAtOffer > 0 ? (
                    <div style={{ marginTop: 4 }}>
                      باقی‌ماندهٔ سفارش: امروز {usd(dv.remainderUsdNow)} (به قیمت روز{' '}
                      {formatRial(dv.remainderRialDayPrice)}) در برابر {usd(dv.remainderUsdAtOffer)}{' '}
                      روزِ پیشنهاد.{' '}
                      {eroded ? (
                        <span className="warn">
                          برای حفظ ارزش دلاری، باقی‌مانده باید {formatRial(dv.dollarHoldRial)} باشد
                          (+{formatRial(dv.gapRial)}). دلار بیش از بها رشد کرده — قیمت‌گذاری همچنان
                          حاشیه‌محور است؛ اگر بخواهید، خودتان قیمت را بالا ببرید.
                        </span>
                      ) : (
                        <span className="muted">قیمت روز ارزش دلاری را پوشش می‌دهد.</span>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })()
          : null}
        {deal && deal.compute.warnings.length ? (
          <div className="banner">
            {deal.compute.warnings.map((w, i) => (
              <div key={i}>⚠ {w}</div>
            ))}
          </div>
        ) : null}

        <div className="row" style={{ marginTop: 12 }}>
          <Button onClick={save} loading={busy}>
            {deal ? 'ذخیرهٔ تغییرات' : 'ثبت پیشنهاد'}
          </Button>
          <Button variant="secondary" onClick={onBack}>
            بازگشت به فهرست
          </Button>
          {deal && canDoc ? (
            <>
              <Button variant="secondary" onClick={() => void saveAndPreview('quote')}>
                🖨 ذخیره و چاپ پیشنهاد قیمت
              </Button>
              <Button variant="secondary" onClick={() => void saveAndPreview('proforma')}>
                🖨 ذخیره و چاپ پیش‌فاکتور
              </Button>
            </>
          ) : null}
          {deal ? (
            // Hand-off (TKT-5): message someone ABOUT this deal — the message carries the deal link so
            // "این قرارداد را امروز ارسال کن" lands with a clickable «↪ قرارداد #N», not loose text.
            <Button
              variant="ghost"
              onClick={() =>
                openMessaging(undefined, {
                  type: 'deal',
                  id: String(deal.id),
                  label: `قرارداد ${deal.no}`,
                })
              }
            >
              💬 پیام دربارهٔ این قرارداد
            </Button>
          ) : null}
        </div>

        {docPreview && deal ? (
          <DocumentPreviewModal
            title={docPreview === 'proforma' ? 'پیش‌نمایش پیش‌فاکتور' : 'پیش‌نمایش پیشنهاد قیمت'}
            url={docUrl(docPreview)}
            downloadUrl={docUrl(docPreview, true)}
            onClose={() => setDocPreview(null)}
          />
        ) : null}

        {deal && legalNext.length ? (
          <div
            className="row"
            style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12 }}
          >
            <span style={{ fontWeight: 700, fontSize: 13 }}>تغییر وضعیت:</span>
            {legalNext.map((s) => (
              <Button key={s} variant="secondary" onClick={() => transition(s)}>
                {STATE_LABELS_FA[s]}
              </Button>
            ))}
          </div>
        ) : null}

        {/* Issue a مودیان tax invoice straight from the sale (E13-10) — no re-hunt in دفترداری, no re-keying.
            Finance-only (the shell passes onIssueInvoice only when the role can post); the invoice panel
            opens prefilled with this deal's buyer + delivered/ordered lines. */}
        {deal && onIssueInvoice && ['Confirmed', 'Delivered', 'Closed'].includes(deal.state) ? (
          <div
            className="row"
            style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12 }}
          >
            <Button variant="secondary" onClick={() => onIssueInvoice(deal.id)}>
              🧾 صدور فاکتور مالیاتی
            </Button>
            <span className="muted" style={{ fontSize: 12 }}>
              خریدار و اقلام از همین قرارداد پر می‌شوند
            </span>
          </div>
        ) : null}

        {/* Prepare a حواله straight from the firm order (FLOW-1) — no re-hunt in ترخیص. Shown only to
            release.prepare roles (the shell passes onPrepareRelease only then) and only while the deal still
            has open lines to dispatch (isReleasePrepEligible — the SAME set PrepareCard's dropdown filters on,
            so a Closed deal never offers a button that would seed an empty card). Lands on ترخیص with
            PrepareCard pre-seeded to this deal + its open-line quantities. */}
        {deal && onPrepareRelease && isReleasePrepEligible(deal.state) ? (
          <div
            className="row"
            style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12 }}
          >
            <Button variant="secondary" onClick={() => onPrepareRelease(deal.id)}>
              🚚 آماده‌سازی حواله
            </Button>
            <span className="muted" style={{ fontSize: 12 }}>
              اقلام باز از همین قرارداد پر می‌شوند
            </span>
          </div>
        ) : null}

        {/* Hand this deal to «دستیار تحلیل» pre-seeded with a question about it (AI-3). Shown only to ai.use
            (Admin — the shell passes onOpenAnalyst only then); no state gate, unlike its siblings — "analyze
            this deal" is valid at ANY state incl. Draft. FILLS the composer (human presses ارسال); the seed
            names deal.no (get_deal's handle), never the internal id. */}
        {deal && onOpenAnalyst ? (
          <div
            className="row"
            style={{ marginTop: 14, borderTop: '1px solid var(--line)', paddingTop: 12 }}
          >
            <Button variant="secondary" onClick={() => onOpenAnalyst(dealAnalysisSeed(deal.no))}>
              🤖 تحلیل با دستیار
            </Button>
            <span className="muted" style={{ fontSize: 12 }}>
              سؤالی دربارهٔ همین قرارداد در دستیار پر می‌شود — خودتان ارسال کنید
            </span>
          </div>
        ) : null}
      </Card>
      {deal ? <DealExtras deal={deal} onChanged={reload} /> : null}
      {bomCode ? <BomModal code={bomCode} onClose={() => setBomCode(null)} /> : null}
    </>
  );
}

/**
 * In-app document preview (the hard-to-print fix): the rendered سند in an iframe, with
 * چاپ / دانلود / تب جدید — see it exactly as it will print, before printing.
 */
function DocumentPreviewModal({
  title,
  url,
  downloadUrl,
  onClose,
}: {
  title: string;
  url: string;
  downloadUrl: string;
  onClose: () => void;
}) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  return (
    <Modal title={title} onClose={onClose}>
      <div className="row" style={{ marginBottom: 8 }}>
        <Button
          onClick={() => {
            // Focus the frame BEFORE printing. Without it some browsers print the
            // (blank) parent window instead of the document — the "no draft" report.
            const w = frameRef.current?.contentWindow;
            w?.focus();
            w?.print();
          }}
        >
          🖨 چاپ / PDF
        </Button>
        <Button variant="secondary" onClick={() => (window.location.href = downloadUrl)}>
          ⬇ ذخیرهٔ فایل (Save as)
        </Button>
        <Button variant="ghost" onClick={() => window.open(url, '_blank')}>
          باز کردن در تب جدید
        </Button>
      </div>
      <iframe
        ref={frameRef}
        src={url}
        title={title}
        style={{
          width: '100%',
          height: '62vh',
          border: '1px solid var(--line)',
          borderRadius: 10,
          background: '#fff',
        }}
      />
    </Modal>
  );
}
